/**
 * DataManager — manages the raw dataset for an Argus Table instance.
 *
 * All mutations flow through this module and trigger the onChange callback
 * so the controller can re-run the render pipeline.
 */
export class DataManager {
  /**
   * @param {Function} onChange - Callback invoked after every data mutation
   */
  constructor(onChange) {
    this._rows = []
    this._onChange = typeof onChange === 'function' ? onChange : () => {}
  }

  /**
   * Replace all data with the provided rows.
   * Non-array input logs a warning and is treated as an empty array.
   *
   * @param {Array<Object>} rows
   */
  load(rows) {
    if (!Array.isArray(rows)) {
      console.warn('[ArgusTable DataManager] load() received non-array input, treating as empty array')
      this._rows = []
    } else {
      this._rows = [...rows]
    }

    this._onChange()
  }

  /**
   * Append rows to the end of the current dataset.
   * Non-array input logs a warning and is treated as an empty array.
   *
   * @param {Array<Object>} rows
   */
  append(rows) {
    if (!Array.isArray(rows)) {
      console.warn('[ArgusTable DataManager] append() received non-array input, treating as empty array')
      this._onChange()
      return
    }

    this._rows = [...this._rows, ...rows]
    this._onChange()
  }

  /**
   * Remove all rows where row[field] === value.
   *
   * @param {string} field - The field name to match against
   * @param {*} value - The value to match for removal
   */
  remove(field, value) {
    this._rows = this._rows.filter((row) => {
      return row[field] !== value
    })

    this._onChange()
  }

  /**
   * Update the row whose `id` matches the given id by merging rowData into it.
   *
   * @param {*} id - The unique ID to find
   * @param {Object} rowData - Properties to merge into the matching row
   */
  update(id, rowData) {
    this._rows = this._rows.map((row) => {
      if (row.id === id) {
        return { ...row, ...rowData }
      }
      return row
    })

    this._onChange()
  }

  /**
   * Merge rowData into the matching stored row (the source of truth) WITHOUT
   * firing the onChange callback, returning snapshots of the row before and
   * after the merge so the caller can decide how to repaint.
   *
   * This exists so the controller can own the repaint decision for the update
   * path (patch-in-place vs. full render) without triggering a redundant full
   * re-render through the change notification. Non-controller callers that want
   * the merge-and-render behavior should keep using `update(id, rowData)`.
   *
   * @param {*} id - The unique ID to find
   * @param {Object} rowData - Properties to merge into the matching row
   * @returns {{ oldRow: Object, newRow: Object } | null} Snapshots of the row
   *   before and after the merge, or `null` when no row matches `id`.
   */
  updateSilently(id, rowData) {
    const index = this._rows.findIndex((row) => row.id === id)
    if (index === -1) {
      return null
    }

    const oldRow = this._rows[index]
    const newRow = { ...oldRow, ...rowData }
    this._rows[index] = newRow

    return { oldRow, newRow }
  }

  /**
   * Return a deep copy of all rows.
   *
   * @returns {Array<Object>}
   */
  getAll() {
    return JSON.parse(JSON.stringify(this._rows))
  }

  /**
   * Remove all rows.
   */
  clear() {
    this._rows = []
    this._onChange()
  }
}
